import { AxiosResponse } from 'axios';
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { ISagaFunc } from 'services/actionConfigs';
import { setPageLoading } from 'widgets/PageLoading';
import api, { IGetPromotionsParams } from './apis';
import { actions } from './slice';
import {
  ICashDiscountIncentiveData,
  IFeeCreditCardData,
  ILoyaltyProgramItem,
  IPromotionDetail,
  IPromotionList,
  IReferralConfig,
  ISettingTaxData,
  IUpdateReferralConfig,
} from './types';
import toast from 'utils/toast';

const getTax = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: ISettingTaxData }> = yield call(
      api.tax.get
    );
    yield put(actions.api.tax.get.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.tax.get.fail(
        error.response?.data?.message || 'Failed to fetch settings tax'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getDiscount = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: ICashDiscountIncentiveData }> = yield call(
      api.discount.get
    );
    yield put(actions.api.discount.get.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.discount.get.fail(
        error.response?.data?.message ||
          'Failed to fetch cash discount incentive'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getFeeCreditCard = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IFeeCreditCardData }> = yield call(
      api.feeCreditCard.get
    );
    yield put(actions.api.feeCreditCard.get.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.feeCreditCard.get.fail(
        error.response?.data?.message || 'Failed to fetch fee credit card'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getLoyalty = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: ILoyaltyProgramItem[] }> = yield call(
      api.loyalty.get
    );
    yield put(actions.api.loyalty.get.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.loyalty.get.fail(
        error.response?.data?.message ||
          'Failed to fetch loyalty program settings'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getPromotions: ISagaFunc<IGetPromotionsParams> = function* ({ payload }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IPromotionList }> = yield call(
      api.promotion.get.list,
      payload
    );
    yield put(actions.api.promotion.get.list.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.promotion.get.list.fail(
        error.response?.data?.message || 'Failed to fetch promotions'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getPromotionDetail: ISagaFunc<string> = function* ({ payload: id }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IPromotionDetail }> = yield call(
      api.promotion.get.detail,
      id
    );
    yield put(actions.api.promotion.get.detail.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.promotion.get.detail.fail(
        error.response?.data?.message || 'Failed to fetch promotion detail'
      )
    );
    toast.error(error?.data?.message ?? 'Failed to fetch data');
  } finally {
    setPageLoading(false);
  }
};

const getReferralSettings = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IReferralConfig[] }> = yield call(
      api.referral.get
    );
    yield put(actions.api.referral.get.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.referral.get.fail(
        error.response?.data?.message || 'Failed to fetch referral settings'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const updateReferralSetting: ISagaFunc<IUpdateReferralConfig[]> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IReferralConfig[] }> = yield call(
      api.referral.update,
      payload
    );
    yield put(actions.api.referral.update.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.referral.update.fail(
        error.response?.data?.message || 'Failed to update referral setting'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

// Watcher Saga
export default function* sagas() {
  // Tax Settings
  yield takeLatest(actions.api.tax.get.fetch.type, getTax);

  // Discount Settings
  yield takeLatest(actions.api.discount.get.fetch.type, getDiscount);

  // Fee Credit Card Settings
  yield takeLatest(actions.api.feeCreditCard.get.fetch.type, getFeeCreditCard);

  // Loyalty Program
  yield takeLatest(actions.api.loyalty.get.fetch.type, getLoyalty);

  // Promotions
  yield takeLatest(actions.api.promotion.get.list.fetch.type, getPromotions);
  yield takeLatest(
    actions.api.promotion.get.detail.fetch.type,
    getPromotionDetail
  );
  
  //referral
  yield takeLatest(actions.api.referral.get.fetch.type, getReferralSettings);
  yield takeLatest(
    actions.api.referral.update.fetch.type,
    updateReferralSetting
  );
}
